/**
 * Pure viewBox math for track map zoom/pan.
 *
 * The track map SVG renders a fixed base viewport (800x600). Zooming shrinks
 * the viewBox window (scale = base.w / vb.w); panning translates it. The
 * aspect ratio stays locked to the base and the window can never leave the
 * base bounds.
 */

export interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const MIN_SCALE = 1;
export const MAX_SCALE = 8;

export function scaleOf(vb: ViewBox, base: ViewBox): number {
  return base.w / vb.w;
}

export function clampViewBox(vb: ViewBox, base: ViewBox): ViewBox {
  const w = Math.min(base.w / MIN_SCALE, Math.max(base.w / MAX_SCALE, vb.w));
  const h = (w * base.h) / base.w;
  const x = Math.min(base.x + base.w - w, Math.max(base.x, vb.x));
  const y = Math.min(base.y + base.h - h, Math.max(base.y, vb.y));
  return { x, y, w, h };
}

export function applyZoom(
  vb: ViewBox,
  base: ViewBox,
  at: { cx: number; cy: number; factor: number },
): ViewBox {
  const w = Math.min(
    base.w / MIN_SCALE,
    Math.max(base.w / MAX_SCALE, vb.w / at.factor),
  );
  const ratio = w / vb.w;
  return clampViewBox(
    {
      x: at.cx - (at.cx - vb.x) * ratio,
      y: at.cy - (at.cy - vb.y) * ratio,
      w,
      h: (w * base.h) / base.w,
    },
    base,
  );
}

export function applyPan(
  vb: ViewBox,
  base: ViewBox,
  by: { dx: number; dy: number },
): ViewBox {
  return clampViewBox({ ...vb, x: vb.x + by.dx, y: vb.y + by.dy }, base);
}

export function formatViewBox(vb: ViewBox): string {
  const round = (n: number) => Math.round(n * 100) / 100;
  return `${round(vb.x)} ${round(vb.y)} ${round(vb.w)} ${round(vb.h)}`;
}
